import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { helpdeskTicketsAPI, helpdeskCategoriesAPI, helpdeskAgentsAPI, helpdeskConfigAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RichPasteArea } from '../../components/RichPasteArea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Plus,
  Ticket,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  AlertCircle,
  Pause,
  CalendarClock,
  User,
  Calendar,
  Tag,
  ChevronRight,
  RefreshCw,
  Upload,
  FileText,
  X,
  Paperclip,
  CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeMX, cn } from '../../lib/utils';
import TicketCleanupPanel from './TicketCleanupPanel';

// File upload constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25 MB
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv'];

// Status configuration (including scheduled)
const STATUS_CONFIG = {
  open: { label: 'Abierto', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: AlertCircle },
  in_progress: { label: 'En proceso', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  on_hold: { label: 'En espera', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Pause },
  scheduled: { label: 'Programado', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: CalendarClock },
  closed: { label: 'Cerrado', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
};

// Priority configuration
// Resultado SLA de un ticket cerrado: cerrado antes del vencimiento = a tiempo (misma regla que Informes)
const getSlaResult = (ticket) => {
  if (ticket.status !== 'closed' || !ticket.closed_at || !ticket.sla_due_date) return null;
  return new Date(ticket.closed_at) < new Date(ticket.sla_due_date) ? 'on_time' : 'late';
};

const PRIORITY_CONFIG = {
  low: { label: 'Baja', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Media', color: 'bg-amber-100 text-amber-700' },
  high: { label: 'Alta', color: 'bg-rose-100 text-rose-700' },
};

const HelpdeskDashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { canWrite: canWritePerm, canAccess: canAccessPerm, isHelpdeskAdmin } = usePermissions();
  const canWriteTickets = isAdmin() || canWritePerm('mesa_de_ayuda', 'dashboard');
  // Phase 3: basic users can also create their own tickets
  const canCreateTicket = isAdmin() || canAccessPerm('mesa_de_ayuda', 'dashboard');
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [agents, setAgents] = useState([]);
  const [userRole, setUserRole] = useState('helpdesk_requester');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Date filters (month/period)
  const [monthFilter, setMonthFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [dateFilterOptions, setDateFilterOptions] = useState({ months: [], periods: [] });
  
  // Create ticket dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category_id: '',
    priority: 'medium',
    requested_date: '',
  });
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Tracks whether the component is still mounted. Prevents in-flight requests
  // from calling setState after the user navigates away (which would otherwise
  // trigger React warnings and, more importantly, surface stale 401s to the
  // global axios interceptor and force a hard reload to /login).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchData();
    fetchDateFilterOptions();
  }, []);
  
  // Refetch tickets when date filters change
  useEffect(() => {
    if (dateFilterOptions.months.length > 0) {
      fetchTickets();
    }
  }, [monthFilter, periodFilter]);

  const fetchDateFilterOptions = async () => {
    try {
      const res = await helpdeskConfigAPI.getDateFilters();
      if (mountedRef.current) setDateFilterOptions(res.data);
    } catch (error) {
      console.error('Error fetching date filters:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      const params = {};
      if (monthFilter !== 'all') {
        params.month = monthFilter;
      } else if (periodFilter !== 'all') {
        params.period_id = periodFilter;
      }
      const ticketsRes = await helpdeskTicketsAPI.getAll(params);
      if (mountedRef.current) setTickets(ticketsRes.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchData = async () => {
    if (mountedRef.current) setLoading(true);
    try {
      // Load each data source independently to prevent cross-module failures
      const results = await Promise.allSettled([
        helpdeskTicketsAPI.getAll(),
        helpdeskCategoriesAPI.getAll(),
        helpdeskAgentsAPI.getUserRole(),
      ]);
      if (!mountedRef.current) return;
      
      const [ticketsResult, categoriesResult, roleResult] = results;
      
      if (ticketsResult.status === 'fulfilled') {
        setTickets(ticketsResult.value.data);
      } else {
        console.error('Error fetching tickets:', ticketsResult.reason);
      }
      
      if (categoriesResult.status === 'fulfilled') {
        setCategories(categoriesResult.value.data.filter(c => c.is_active));
      } else {
        console.error('Error fetching categories:', categoriesResult.reason);
        setCategories([]);
      }
      
      if (roleResult.status === 'fulfilled') {
        setUserRole(roleResult.value.data.role);
      } else {
        console.error('Error fetching user role:', roleResult.reason);
      }
      
      // Get agents if admin de módulo (admin global o miembro del Equipo TI) - non-blocking
      if (isAdmin() || isHelpdeskAdmin) {
        try {
          const agentsRes = await helpdeskAgentsAPI.getAll();
          if (mountedRef.current) setAgents(agentsRes.data);
        } catch (error) {
          console.error('Error fetching agents:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };
  
  // Handler to clear date filters
  const clearDateFilters = () => {
    setMonthFilter('all');
    setPeriodFilter('all');
    fetchData(); // Refresh without date filter
  };

  // File validation helpers
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileExtension = (filename) => {
    return '.' + filename.split('.').pop().toLowerCase();
  };

  const validateFiles = useCallback((files) => {
    const errors = [];
    const validFiles = [];
    
    // Check if adding these files would exceed max count
    const totalCount = selectedFiles.length + files.length;
    if (totalCount > MAX_FILES) {
      errors.push(`Maximo ${MAX_FILES} archivos permitidos`);
      return { validFiles: [], errors };
    }
    
    // Calculate current total size
    const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    let newTotalSize = currentTotalSize;
    
    for (const file of files) {
      // Check extension
      const ext = getFileExtension(file.name);
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        errors.push(`"${file.name}": Extension no permitida (${ext})`);
        continue;
      }
      
      // Check individual file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}": Excede 5MB (${formatFileSize(file.size)})`);
        continue;
      }
      
      // Check total size
      if (newTotalSize + file.size > MAX_TOTAL_SIZE) {
        errors.push(`Total excedería 25MB. No se puede agregar "${file.name}"`);
        continue;
      }
      
      // Check for duplicates
      if (selectedFiles.some(f => f.name === file.name)) {
        errors.push(`"${file.name}" ya está agregado`);
        continue;
      }
      
      newTotalSize += file.size;
      validFiles.push(file);
    }
    
    return { validFiles, errors };
  }, [selectedFiles]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const { validFiles, errors } = validateFiles(files);
    
    if (errors.length > 0) {
      setFileErrors(errors);
    } else {
      setFileErrors([]);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
    
    // Reset input
    e.target.value = '';
  }, [validateFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    
    const { validFiles, errors } = validateFiles(files);
    
    if (errors.length > 0) {
      setFileErrors(errors);
    } else {
      setFileErrors([]);
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  }, [validateFiles]);

  const removeFile = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileErrors([]);
  }, []);

  const clearFiles = useCallback(() => {
    setSelectedFiles([]);
    setFileErrors([]);
  }, []);

  const getTotalFileSize = () => {
    return selectedFiles.reduce((sum, f) => sum + f.size, 0);
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    // Strip HTML tags for validation check
    const descText = newTicket.description.replace(/<[^>]*>/g, '').trim();
    if (!newTicket.title.trim() || !descText || !newTicket.category_id) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    // Final validation
    if (fileErrors.length > 0) {
      toast.error('Corrige los errores de archivos antes de continuar');
      return;
    }
    
    setCreating(true);
    try {
      // Build request data, only include requested_date if set
      const requestData = {
        ...newTicket,
        requested_date: newTicket.requested_date || null
      };
      
      // Create ticket first
      const ticketResponse = await helpdeskTicketsAPI.create(requestData);
      const ticketId = ticketResponse.data.id;
      
      // Upload attachments if any
      if (selectedFiles.length > 0) {
        try {
          await helpdeskTicketsAPI.uploadAttachments(ticketId, selectedFiles);
          toast.success(`Ticket creado con ${selectedFiles.length} archivo(s)`);
        } catch (uploadError) {
          console.error('Error uploading attachments:', uploadError);
          toast.warning('Ticket creado, pero hubo un error al subir algunos archivos');
        }
      } else {
        toast.success('Ticket creado correctamente');
      }
      
      setIsCreateDialogOpen(false);
      setNewTicket({ title: '', description: '', category_id: '', priority: 'medium', requested_date: '' });
      setSelectedFiles([]);
      setFileErrors([]);
      fetchData();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el ticket');
    } finally {
      setCreating(false);
    }
  };

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter !== 'all' && ticket.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && ticket.priority !== priorityFilter) return false;
    if (categoryFilter !== 'all' && ticket.category_id !== categoryFilter) return false;
    if (assigneeFilter === 'unassigned' && ticket.assigned_to) return false;
    if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && ticket.assigned_to !== assigneeFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.title.toLowerCase().includes(query) ||
        ticket.folio.toLowerCase().includes(query) ||
        ticket.requester_name.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Opciones "Asignado a": Equipo TI + cualquier agente presente en los tickets
  const assigneeOptions = Object.values(
    [...agents.map(a => ({ id: a.id, name: a.name })),
     ...tickets.filter(t => t.assigned_to).map(t => ({ id: t.assigned_to, name: t.assigned_to_name || 'Sin nombre' }))]
      .reduce((acc, a) => ({ ...acc, [a.id]: acc[a.id] || a }), {})
  ).sort((a, b) => a.name.localeCompare(b.name));

  // Resumen SLA del listado filtrado (misma regla que Informes: cierre antes del vencimiento)
  const slaSummary = filteredTickets.reduce((acc, t) => {
    const r = getSlaResult(t);
    if (r) acc[r] += 1;
    if (t.status !== 'closed' && t.sla_due_date && new Date(t.sla_due_date) < new Date()) acc.open_late += 1;
    return acc;
  }, { on_time: 0, late: 0, open_late: 0 });

  // Stats
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-800">
              Mesa de Ayuda
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {userRole === 'helpdesk_admin' && 'Administración de tickets'}
              {userRole === 'helpdesk_agent' && 'Mis tickets asignados'}
              {userRole === 'helpdesk_requester' && 'Mis solicitudes'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(user?.is_master || isAdmin()) && (
              <TicketCleanupPanel
                tickets={tickets}
                onDeleted={fetchData}
                canDelete={!!user?.is_master}
                canAssign={isAdmin()}
                agents={agents}
              />
            )}
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            {canCreateTicket && (
              <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="create-ticket-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Ticket
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Ticket className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{stats.open}</p>
                  <p className="text-xs text-slate-500">Abiertos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.inProgress}</p>
                  <p className="text-xs text-slate-500">En proceso</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats.closed}</p>
                  <p className="text-xs text-slate-500">Cerrados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por título, folio o solicitante..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <TooltipProvider delayDuration={200}>
                  {/* Status Filter */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                          <SelectTrigger className="w-32" aria-label="Filtro de estatus">
                            <SelectValue placeholder="Estado" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="open">Abiertos</SelectItem>
                            <SelectItem value="in_progress">En proceso</SelectItem>
                            <SelectItem value="on_hold">En espera</SelectItem>
                            <SelectItem value="scheduled">Programados</SelectItem>
                            <SelectItem value="closed">Cerrados</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Filtro: Estatus</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Priority Filter */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                          <SelectTrigger className="w-32" aria-label="Filtro de prioridad">
                            <SelectValue placeholder="Prioridad" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            <SelectItem value="low">Baja</SelectItem>
                            <SelectItem value="medium">Media</SelectItem>
                            <SelectItem value="high">Alta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Filtro: Prioridad</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Category Filter */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                          <SelectTrigger className="w-40" aria-label="Filtro de categoría">
                            <SelectValue placeholder="Categoría" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            {categories.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Filtro: Categoría</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Asignado a Filter */}
                  {userRole !== 'helpdesk_requester' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                            <SelectTrigger className="w-44" aria-label="Filtro de asignado" data-testid="assignee-filter">
                              <SelectValue placeholder="Asignado a" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los agentes</SelectItem>
                              {assigneeOptions.map(a => (
                                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                              ))}
                              <SelectItem value="unassigned">Sin asignar</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>Filtro: Asignado a</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  
                  {/* Date separator */}
                  <div className="w-px h-6 bg-slate-200 mx-1" />
                  <CalendarDays className="w-4 h-4 text-slate-400" />
                  
                  {/* Month Filter */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select 
                          value={monthFilter} 
                          onValueChange={(v) => {
                            setMonthFilter(v);
                            if (v !== 'all') setPeriodFilter('all'); // Clear period when month is selected
                          }}
                        >
                          <SelectTrigger className="w-36" aria-label="Filtro por mes" data-testid="month-filter">
                            <SelectValue placeholder="Mes" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los meses</SelectItem>
                            {dateFilterOptions.months.map(m => (
                              <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Filtro: Mes (fecha creación)</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Period Filter */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select 
                          value={periodFilter} 
                          onValueChange={(v) => {
                            setPeriodFilter(v);
                            if (v !== 'all') setMonthFilter('all'); // Clear month when period is selected
                          }}
                        >
                          <SelectTrigger className="w-32" aria-label="Filtro por periodo" data-testid="period-filter">
                            <SelectValue placeholder="Periodo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {dateFilterOptions.periods.slice(0, 20).map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Filtro: Periodo del calendario</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Clear date filters button */}
                  {(monthFilter !== 'all' || periodFilter !== 'all') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearDateFilters}
                      className="text-slate-500 hover:text-slate-700 h-9 px-2"
                      data-testid="clear-date-filters"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </TooltipProvider>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Date filter active indicator */}
        {(monthFilter !== 'all' || periodFilter !== 'all') && (
          <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 rounded-lg px-4 py-2">
            <CalendarDays className="w-4 h-4" />
            <span>
              Filtrando por: {monthFilter !== 'all' 
                ? dateFilterOptions.months.find(m => m.key === monthFilter)?.label 
                : dateFilterOptions.periods.find(p => p.id === periodFilter)?.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearDateFilters}
              className="text-purple-700 hover:text-purple-900 h-6 px-2 ml-auto"
            >
              Limpiar filtro
            </Button>
          </div>
        )}

        {/* Tickets List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">
              Tickets ({filteredTickets.length})
            </CardTitle>
            {userRole !== 'helpdesk_requester' && (
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 pt-1" data-testid="sla-summary">
                <span>Cerrados a tiempo: <strong className="text-emerald-600" data-testid="sla-summary-on-time">{slaSummary.on_time}</strong></span>
                <span>Cerrados fuera de tiempo: <strong className="text-rose-600" data-testid="sla-summary-late">{slaSummary.late}</strong></span>
                <span>Abiertos con SLA vencido: <strong className="text-amber-600" data-testid="sla-summary-open-late">{slaSummary.open_late}</strong></span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {filteredTickets.length === 0 ? (
              <div className="text-center py-12">
                <Ticket className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No hay tickets</p>
                <p className="text-sm text-slate-400 mt-1">
                  {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all'
                    ? 'No se encontraron tickets con los filtros aplicados'
                    : 'Crea tu primer ticket para comenzar'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTickets.map(ticket => {
                  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                  const StatusIcon = statusConfig.icon;
                  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
                  const slaExpired = ticket.sla_due_date && ticket.status !== 'closed' && new Date(ticket.sla_due_date) < new Date();
                  const slaOk = ticket.sla_due_date && ticket.status !== 'closed' && new Date(ticket.sla_due_date) >= new Date();
                  const closedSla = ticket.status === 'closed' ? getSlaResult(ticket) : null;
                  
                  return (
                    <div
                      key={ticket.id}
                      className="border rounded-lg p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/helpdesk/tickets/${ticket.id}`)}
                      data-testid={`ticket-row-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-slate-400">#{ticket.folio}</span>
                            <Badge className={cn('text-xs', statusConfig.color)}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            <Badge className={cn('text-xs', priorityConfig.color)}>
                              {priorityConfig.label}
                            </Badge>
                            {slaOk && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`sla-ok-${ticket.id}`}>
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                SLA
                              </span>
                            )}
                            {slaExpired && (
                              <span className="inline-flex items-center gap-1 text-xs text-rose-600 font-medium" data-testid={`sla-expired-${ticket.id}`}>
                                <span className="w-2 h-2 rounded-full bg-rose-500" />
                                SLA
                              </span>
                            )}
                            {closedSla === 'on_time' && (
                              <span className="text-xs text-emerald-600" data-testid={`sla-closed-on-time-${ticket.id}`}>A tiempo</span>
                            )}
                            {closedSla === 'late' && (
                              <span className="text-xs text-rose-600 font-medium" data-testid={`sla-closed-late-${ticket.id}`}>Fuera de tiempo</span>
                            )}
                          </div>
                          <h3 className="font-medium text-slate-800 truncate">{ticket.title}</h3>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {ticket.category_name || 'Sin categoría'}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.requester_name}
                            </span>
                            <span className="flex items-center gap-1" data-testid={`ticket-created-at-${ticket.id}`}>
                              <Calendar className="w-3 h-3" />
                              {formatDateTimeMX(ticket.created_at)}
                            </span>
                            {ticket.assigned_to_name && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <User className="w-3 h-3" />
                                Asignado: {ticket.assigned_to_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Ticket Dialog - Fixed overflow with scrollable body */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 py-4 border-b shrink-0">
              <DialogTitle className="font-['Manrope']">Nuevo Ticket</DialogTitle>
              <DialogDescription>
                Crea una nueva solicitud de soporte
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    placeholder="Describe brevemente tu solicitud"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    data-testid="ticket-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Categoría *</Label>
                  <Select
                    value={newTicket.category_id}
                    onValueChange={(v) => setNewTicket({ ...newTicket, category_id: v })}
                  >
                    <SelectTrigger data-testid="ticket-category-select">
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Prioridad</Label>
                  <Select
                    value={newTicket.priority}
                    onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                  >
                    <SelectTrigger data-testid="ticket-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem>
                      <SelectItem value="medium">Media</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="requested_date">Fecha solicitada (opcional)</Label>
                  <div className="relative">
                    <Input
                      id="requested_date"
                      type="date"
                      value={newTicket.requested_date}
                      onChange={(e) => setNewTicket({ ...newTicket, requested_date: e.target.value })}
                      className="pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                      data-testid="ticket-requested-date-input"
                    />
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  <p className="text-xs text-slate-500">
                    Fecha en que necesitas que se resuelva tu solicitud
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción *</Label>
                  <RichPasteArea
                    value={newTicket.description}
                    onChange={(html) => setNewTicket({ ...newTicket, description: html })}
                    placeholder="Describe detalladamente tu solicitud..."
                    className="min-h-[100px]"
                    data-testid="ticket-description-input"
                  />
                </div>
                
                {/* File Upload Section */}
                <div className="space-y-2">
                  <Label>Evidencia (opcional)</Label>
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
                      isDragging ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300",
                      selectedFiles.length >= MAX_FILES && "opacity-50 cursor-not-allowed"
                    )}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept={ALLOWED_EXTENSIONS.join(',')}
                      disabled={selectedFiles.length >= MAX_FILES}
                      data-testid="file-upload-input"
                    />
                    <label
                      htmlFor="file-upload"
                      className={cn(
                        "cursor-pointer flex flex-col items-center gap-2",
                        selectedFiles.length >= MAX_FILES && "cursor-not-allowed"
                      )}
                    >
                      <Upload className="w-8 h-8 text-slate-400" />
                      <div className="text-sm text-slate-600">
                        <span className="text-blue-600 font-medium">Haz clic para seleccionar</span>
                        {' '}o arrastra archivos aquí
                      </div>
                      <p className="text-xs text-slate-400">
                        Max. {MAX_FILES} archivos, 5MB c/u, 25MB total
                      </p>
                      <p className="text-xs text-slate-400">
                        PDF, PNG, JPG, DOC, DOCX, XLS, XLSX, TXT
                      </p>
                    </label>
                  </div>
                  
                  {/* File Errors */}
                  {fileErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-700">
                          {fileErrors.map((err, i) => (
                            <div key={i}>{err}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 font-medium">
                          <Paperclip className="w-4 h-4 inline mr-1" />
                          {selectedFiles.length} archivo(s) seleccionado(s)
                        </span>
                        <span className="text-slate-500">
                          Total: {formatFileSize(getTotalFileSize())}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm"
                            data-testid={`file-item-${index}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <span className="truncate text-slate-700">{file.name}</span>
                              <span className="text-slate-400 text-xs flex-shrink-0">
                                ({formatFileSize(file.size)})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-slate-400 hover:text-red-500 p-1 rounded transition-colors"
                              data-testid={`remove-file-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearFiles}
                        className="text-slate-500 hover:text-red-500"
                      >
                        Quitar todos
                      </Button>
                    </div>
                  )}
                </div>
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white">
                <Button type="button" variant="outline" onClick={() => {
                  setIsCreateDialogOpen(false);
                  setSelectedFiles([]);
                  setFileErrors([]);
                }}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={creating || fileErrors.length > 0} 
                  data-testid="submit-ticket-btn"
                >
                  {creating ? 'Creando...' : selectedFiles.length > 0 ? `Crear con ${selectedFiles.length} archivo(s)` : 'Crear Ticket'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HelpdeskDashboard;
